import { FilterMenu } from "@gaiaProfessionals/Components/HCP/WebinarFilterMenu";
import { WebinarTile } from "@gaiaProfessionals/Components/HCP/WebinarTile";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import React, { ReactElement, useEffect, useState } from "react";

interface IWebinar {
  Title?: string
  Summary?: string
  Author0?: string
  FilterTags?: string[]
}

interface WebinarStyles {
  mobileViewWrapper?: any
  filterButtonWrapper?: any
  filterButton?: any
  filterMenuWrapper?: any
  filterComponentWrapper?: any
  desktopViewWrapper?: any
  webinarsListWrapper?: any
  filterComponentWrapperTablet?: any
  tabletSearchInput?: any
}

export const WebinarStyles: WebinarStyles = {
    mobileViewWrapper: {
      marginTop: "1em",
    },
    filterButtonWrapper: {
      textAlign: "center",
    },
    filterButton: {
      padding: "0.5em",
      backgroundColor: "#205C40",
      border: "none",
      color: '#FFFFFF',
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 400,
      fontFamily: 'Lato',
      borderRadius: '.375rem',
      width: '90%'
    },
    filterMenuWrapper: {
      marginTop: "2.5em",
    },
    desktopViewWrapper: {
      display: "flex",
      marginTop: "1em",
    },
    filterComponentWrapper: {
      flex: 0.5,
    },
    filterComponentWrapperTablet: {
      width: '13em'
    },
    webinarsListWrapper: {
      flex: 2,
      maxHeight: "100vh",
      overflowY: "auto",
      marginTop: "2.5em",
    },
  };

const WebinarWidget: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilters, setCategoryFilters] = useState([]);
    const [sortBy, setSortBy] = useState("");
    const [webinarPlaying, setWebinarPlaying] = useState(false);
    const [activeWebinar, setActiveWebinar] = useState({})
    const [webinarData, setWebinarData] = useState<any[]>([])
    const [filteredWebinarData, setFilteredWebinarData] = useState<IWebinar[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
    const [isMobileView, setIsMobileView] = useState(false);
    const [isTabletView, setIsTabletView] = useState(false)
    const [isDesktopView, setIsDesktopView] = useState(true)
    const [categoryList, setCategoryList] = useState([])

    const azureUrl = `https://hcpwebinars.azurewebsites.net/api/HandleClientRequest?page=${page}`

    const buildFilterTagList = (webinars: any[]): void => {
      const filtersArr = webinars.map(w => (w.fields.FilterTags))
      const masterList = filtersArr.reduce((acc, nestedArr) => {
        nestedArr?.forEach((filter: any) => { // Use 'any' if you're not sure about the types in 'nestedArr'
          if (typeof filter === 'string' && !acc.includes(filter)) {
            acc.push(filter)
          }
        })
        return acc
      }, [])
      setCategoryList(masterList)
    }
    

    const updateIsMobileView = () => {
      setIsMobileView(window.innerWidth <= 450)
      setIsTabletView(window.innerWidth > 451 && window.innerWidth < 1201)
      setIsDesktopView(window.innerWidth > 1200)
    }
    // Fetch Webinar's from Azure SharePoint
    useEffect(() => {
      const loadWebinars = async () => {

        try {
          const response = await fetch(azureUrl, {
            method: 'GET',
          })
          if (!response.ok) {
            throw new Error('Failed to fetch webinars')
          }
          const data = await response.json()

          setWebinarData([...data])
        } catch (error) {
          // Handle error if any
          console.error('Error fetching webinars:', error)
        }
      }
    
      loadWebinars()
    }, [])

    useEffect(() => {
      buildFilterTagList(webinarData)
    }, [webinarData])

    // Add event listener for screen resize
    useEffect(() => {
      updateIsMobileView()
      window.addEventListener("resize", updateIsMobileView)

    // Clean up the event listener on component unmount
      return () => {
        window.removeEventListener("resize", updateIsMobileView)
      }
    }, [])

    // Debounce keyword input
    useEffect(() => {

      // let filteredData = webinarData.map((webinar: any) => webinar.fields)
      let filteredData = webinarData
      if (searchTerm || categoryFilters.length > 0) {
        filteredData = fuzzySearch(searchTerm, filteredData, categoryFilters)
      }

      setFilteredWebinarData(filteredData)
    }, [searchTerm, categoryFilters, webinarData])
    
    
    // This is currently a basic filter/search function. In the future we have
    // the option to set up fuzzy search functionality

    const fuzzySearch = (term: string, data: any, categoryFilters: string[]) => {
      const lowerTerm = term.toLowerCase();
      return data.filter((item: any) => {
        const activeSearch = term !== ''
        const activeFilter = categoryFilters.length > 0
        // Perform the fuzzy search on the Title, Summary, and Author fields
        const matchesSearchTerm = term === "" ? true :
          item.fields.Title?.toLowerCase().includes(lowerTerm)
          || item.fields.Summary?.toLowerCase().includes(lowerTerm)
          || item.fields.Author0?.toLowerCase().includes(lowerTerm);

        // Check if any of the category filters match any of the item's FilterTags
        const matchesCategoryFilter = categoryFilters.length === 0 ? true :
          categoryFilters.some((filter) =>
            item.fields.FilterTags?.includes(filter)
          );
    
        // Include the item in the results based on active filters/search
        if (activeSearch && !activeFilter) {
          return matchesSearchTerm
        }
        if (activeFilter && !activeSearch) {
          return matchesCategoryFilter
        }
        if (activeFilter && activeSearch) {
          return matchesSearchTerm && matchesCategoryFilter;
        }
        return true
      });
    };

    const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(event.target.value);
    };

    const handleCategoryFiltersChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;

        setCategoryFilters((prevFilters: any) => {
            if (prevFilters.includes(value)) {
                return prevFilters.filter((filter: string) => filter !== value);
            } 
                return [...prevFilters, value];
            
        });
    };
      
    const handleSortByChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
      setSortBy(event.target.value);
    };

    const handleToggleWebinar = (event: React.MouseEvent<HTMLDivElement>): void => {
      const target = event.target as HTMLDivElement
      const stringData = target.dataset.activeWebinar || ''
      const webinar = JSON.parse(stringData)
      
      setActiveWebinar({...webinar})
      setWebinarPlaying(!webinarPlaying)
    }

    
    return (
      <div>
        {isMobileView && (
          <div style={WebinarStyles.mobileViewWrapper}>
            {/* Mobile Filter Component (Collapsible) */}
            <div style={WebinarStyles.filterButtonWrapper}>
              <button onClick={() => setIsFilterMenuOpen(prev => !prev)} style={WebinarStyles.filterButton}>
                Filter
              </button>
            </div>
            {isFilterMenuOpen && (
              <div style={WebinarStyles.filterMenuWrapper}>
                <FilterMenu
                  categories={categoryList}
                  categoryFilters={categoryFilters}
                  handleCategoryFiltersChange={handleCategoryFiltersChange}
                  handleSearchTermChange={handleSearchTermChange}
                  handleSortByChange={handleSortByChange}
                  isTabletView={isTabletView}
                  searchTerm={searchTerm}
                  sortBy={sortBy}
                />
              </div>
            )}
    
            {/* Webinars List goes here (Vertically Stacked) */}
            <div style={WebinarStyles.filterMenuWrapper}>
              {webinarData &&
                filteredWebinarData.map((webinar, index) => (
                  <WebinarTile
                    key={index}
                    isDesktopView={isDesktopView}
                    isMobileView={isMobileView}
                    isTabletView={isTabletView}
                    categoryFilters={categoryFilters}
                    handleToggleWebinar={handleToggleWebinar}
                    searchTerm={searchTerm}
                    sortBy={sortBy}
                    webinar={webinar}
                  />
                ))}
            </div>
          </div>
        )}
    
        {/* Desktop View */}
        {!isMobileView && (
          <div style={WebinarStyles.desktopViewWrapper}>
            <div style={ isTabletView ? WebinarStyles.filterComponentWrapperTablet : WebinarStyles.filterComponentWrapper }>
              {/* Filter Component goes here */}
              <FilterMenu
                categories={categoryList}
                categoryFilters={categoryFilters}
                handleCategoryFiltersChange={handleCategoryFiltersChange}
                handleSearchTermChange={handleSearchTermChange}
                isTabletView={isTabletView}
                handleSortByChange={handleSortByChange}
                searchTerm={searchTerm}
                sortBy={sortBy}
              />
            </div>
            <div style={WebinarStyles.webinarsListWrapper}>
              {/* Webinars List goes here */}
              {webinarData &&
                filteredWebinarData.map((webinar, index) => (
                  <WebinarTile
                    key={index}
                    categoryFilters={categoryFilters}
                    handleToggleWebinar={handleToggleWebinar}
                    isMobileView={isMobileView}
                    isDesktopView={isDesktopView}
                    isTabletView={isTabletView}
                    searchTerm={searchTerm}
                    sortBy={sortBy}
                    webinar={webinar}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    );
};

const WebinarsDev: WidgetModule = {
    component: WebinarWidget,
    definition: {
        group: "Common",
        displayName: "Webinars Container",
        fieldDefinitions: [],
    },
};

export default WebinarsDev;